import React, { useState, useEffect, useMemo } from "react";
import { Joyride, STATUS, ACTIONS } from "react-joyride";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "itotal4u.tour.completed";

/** Tour interactivo de bienvenida (react-joyride).
 *
 * Se dispara MANUALMENTE: cuando algún componente emite el evento
 * `window.dispatchEvent(new Event("itotal4u:start-tour"))`.
 *
 * Pasos adaptados por rol:
 *  - Staff (agent/admin/super_*) ve los pasos administrativos.
 *  - Customer solo ve los pasos relevantes a su perfil.
 */
export default function OnboardingTour() {
    const { user } = useAuth();
    const [run, setRun] = useState(false);

    // Listen for external start event (botón "Tour" en el topbar / Perfil).
    useEffect(() => {
        const handler = () => setRun(true);
        window.addEventListener("itotal4u:start-tour", handler);
        return () => window.removeEventListener("itotal4u:start-tour", handler);
    }, []);

    const steps = useMemo(() => {
        if (!user) return [];
        const role = user.role;
        const isStaff = ["agent", "admin", "superadmin", "super_agent"].includes(role);
        const isAdmin = ["admin", "superadmin"].includes(role);
        const isInternal = !!user.is_internal_use;

        const list = [
            {
                target: '[data-testid="nav-dashboard"]',
                title: `¡Bienvenido, ${user.name}! 👋`,
                content: "Este es tu Dashboard — un resumen rápido de tickets abiertos, SLA roto, asignados a ti y la actividad reciente.",
                placement: "right",
                skipBeacon: true,
            },
            {
                target: '[data-testid="nav-tickets"]',
                title: "Bandeja de tickets",
                content: isStaff
                    ? "Aquí ves la bandeja completa con filtros (estado, prioridad, asignado, etc). Puedes guardar vistas favoritas para acceso rápido."
                    : "Aquí ves todos tus tickets. Puedes crear uno nuevo con el botón 'Nuevo ticket' arriba a la derecha.",
                placement: "right",
            },
        ];

        if (isStaff) {
            list.push({
                target: '[data-testid="nav-reports"]',
                title: "Reportes 📊",
                content: "Reportes guardables con filtros avanzados. Puedes programar uno semanal por correo para tu jefe.",
                placement: "right",
            });
        }

        if (isAdmin) {
            list.push(
                {
                    target: '[data-testid="nav-companies"]',
                    title: "Catálogo de Empresas",
                    content: "Gestiona empresas clientes. Aquí puedes marcar 'Confidencial' (solo admins explícitos las ven) y 'Uso Interno' (perímetro segregado para tu equipo).",
                    placement: "right",
                },
                {
                    target: '[data-testid="nav-agents"]',
                    title: "Personas",
                    content: "Aquí gestionas todos los usuarios: clientes, agentes, súper agentes, admins. Cada uno se asigna a empresas con permisos granulares.",
                    placement: "right",
                },
                {
                    target: '[data-testid="nav-ticket-templates"]',
                    title: "Tipos de solicitud",
                    content: "Plantillas reutilizables. Define un tipo (ej. 'Solicitar acceso a SAP') y todos los usuarios autorizados lo verán al crear ticket — con asunto y descripción prellenados.",
                    placement: "right",
                },
            );
        }

        if (isInternal) {
            list.push({
                target: '[data-testid="nav-internal-dashboard"]',
                title: "Mundo Interno 🛡",
                content: "Como tienes el flag 'Uso Interno', puedes acceder al perímetro interno: KPIs y empresas internas separadas del catálogo principal. El botón flotante naranja te permite crear tickets internos rápidamente.",
                placement: "right",
            });
        }

        list.push(
            {
                target: '[data-testid="nav-profile"]',
                title: "Tu perfil",
                content: "Aquí cambias tu contraseña, configuras tu tema (claro/oscuro) y revisas tus preferencias.",
                placement: "right",
            },
            {
                target: "body",
                title: "¡Listo! 🎉",
                content: "Eso es lo básico. Pasa el cursor sobre los íconos ⓘ para ver explicaciones rápidas, y si tienes dudas, contacta al admin del proyecto. ¡Bienvenido a bordo!",
                placement: "center",
            },
        );

        return list;
    }, [user]);

    const handleEvent = (data) => {
        const { status, action, type } = data || {};
        // Cerrar (X), terminar o skip → fin del tour.
        // En react-joyride v3 el botón Cerrar emite action=CLOSE (avanza el index pero
        // queremos terminar). FINISHED/SKIPPED son los estados terminales del tour.
        if (
            [STATUS.FINISHED, STATUS.SKIPPED].includes(status) ||
            action === ACTIONS.CLOSE ||
            type === "tour:end"
        ) {
            setRun(false);
            if (user) {
                localStorage.setItem(`${STORAGE_KEY}.${user.user_id}`, "1");
            }
        }
    };

    if (!user) return null;

    return (
        <Joyride
            steps={steps.map(s => ({ ...s, skipBeacon: true }))}
            run={run}
            continuous
            showSkipButton
            showProgress
            disableScrolling={false}
            scrollToFirstStep
            onEvent={handleEvent}
            locale={{
                back: "Atrás",
                close: "Cerrar",
                last: "Finalizar",
                next: "Siguiente",
                open: "Abrir",
                skip: "Omitir",
            }}
            styles={{
                options: {
                    arrowColor: "var(--bg-1, #17171A)",
                    backgroundColor: "var(--bg-1, #17171A)",
                    primaryColor: "var(--accent, #DC2626)",
                    textColor: "var(--text, #F5F5F5)",
                    overlayColor: "rgba(0, 0, 0, 0.55)",
                    zIndex: 10000,
                },
                tooltip: {
                    borderRadius: 10,
                    border: "1px solid var(--border-strong, #3A3A40)",
                    boxShadow: "0 16px 48px -12px rgba(0,0,0,.55)",
                    fontSize: 14,
                    padding: 18,
                },
                tooltipTitle: {
                    fontSize: 16,
                    fontWeight: 700,
                    marginBottom: 6,
                    color: "var(--text, #F5F5F5)",
                },
                tooltipContent: {
                    color: "var(--text-muted, #A3A3A8)",
                    lineHeight: 1.55,
                },
                buttonNext: {
                    borderRadius: 8,
                    fontSize: 13,
                    padding: "8px 16px",
                    fontWeight: 600,
                },
                buttonBack: {
                    color: "var(--text-muted, #A3A3A8)",
                    fontSize: 13,
                    marginRight: 8,
                },
                buttonSkip: {
                    color: "var(--text-dim, #737373)",
                    fontSize: 12,
                },
            }}
        />
    );
}

/** Helper para reiniciar el tour desde el perfil del usuario. */
export function resetTourForUser(userId) {
    localStorage.removeItem(`${STORAGE_KEY}.${userId}`);
}

/** Helper para disparar el tour manualmente (botón Tour del topbar / Profile). */
export function startTour() {
    window.dispatchEvent(new Event("itotal4u:start-tour"));
}
